import {
  EmptyHint,
  Panel,
  formatNumber,
  formatPercent,
  getRankTier,
} from "./statsUi"

const medalColor = (index: number) => {
  if (index === 0) return "#FBBF24"
  if (index === 1) return "#CBD5E1"
  if (index === 2) return "#D97706"
  return "rgba(255,255,255,0.3)"
}

// The global ladder, ordered by rating. The viewer's own row is highlighted and
// called out beneath the table when they placed outside the top slice.
const LeaderboardTab: React.FC<{
  leaderboard: ILeaderboardEntry[]
  loading: boolean
  myEmail: string
  myRating: number
}> = ({ leaderboard, loading, myEmail, myRating }) => {
  const myIndex = leaderboard.findIndex((entry) => entry.email === myEmail)

  return (
    <Panel
      title="Global Leaderboard"
      subtitle="Ranked by rating across every finished game"
      icon="fa-ranking-star"
      accent="#FBBF24"
    >
      {loading ? (
        <EmptyHint text="Loading the ladder…" />
      ) : leaderboard.length === 0 ? (
        <EmptyHint text="Nobody has finished a game yet. Be the first." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] mobile:min-w-[480px] text-left">
              <thead>
                <tr className="border-b border-white/10 text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">#</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Player</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Rank</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Rating</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Games</th>
                  <th className="pb-2 mobile:pb-[6px] font-semibold">Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, index) => {
                  const tier = getRankTier(entry.rating)
                  const isMe = entry.email === myEmail
                  return (
                    <tr
                      key={entry.email}
                      className={`border-b border-white/5 text-sm sm:text-xs mobile:text-[12px]/[1.4] last:border-b-0 ${
                        isMe ? "bg-amber-500/10" : ""
                      }`}
                    >
                      <td
                        className="py-2.5 mobile:py-[7px] pr-3 font-fourth font-bold"
                        style={{ color: medalColor(index) }}
                      >
                        {index + 1}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/90">
                        {entry.username || entry.email}
                        {isMe && (
                          <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[0.6rem] mobile:text-[9px]/[1.4] uppercase tracking-wider text-amber-200">
                            you
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3" style={{ color: tier.color }}>
                        <i className={`fas ${tier.icon} mr-1.5 text-[0.7rem] mobile:text-[10px]/[1.4]`} />
                        {tier.name}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 font-semibold text-amber-200">
                        {formatNumber(entry.rating)}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/60">
                        {formatNumber(entry.gamesPlayed)}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] text-white/60">
                        {formatPercent(
                          entry.gamesPlayed > 0
                            ? (entry.gamesWon / entry.gamesPlayed) * 100
                            : 0,
                          0
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {myIndex === -1 && (
            <p className="mt-3 text-xs mobile:text-[11px]/[1.4] text-white/40">
              You are not in the top {leaderboard.length} yet — your rating is{" "}
              <span className="font-semibold text-amber-200">
                {formatNumber(myRating)}
              </span>
              .
            </p>
          )}
        </>
      )}
    </Panel>
  )
}

export default LeaderboardTab
